<app-section-quick-actions section="item" />

<header class="atmos-page__header">
  <h1 class="atmos-page__title">Unlock calculator</h1>
</header>

<div class="atmos-page__intro">
  <p class="atmos-paragraph">
    You can use this tool to calculate the cost of unlocking items.
    For all your selected items, the most recent price will be used to calculate the currencies you need.<br/>
    Items you already own will not be counted.<br/>
    If you want to set items as a goal to save towards, have a look at at the <a class="atmos-text-link" routerLink="/item/goals">Goals</a> page.
  </p>
</div>

<!-- Selection -->
<section class="atmos-card">
  <div class="atmos-card__body">
    <h2 class="atmos-section__title">Selection</h2>
    @if (!items.length) {
      <p class="atmos-paragraph">No items selected. Search for items below.</p>
    } @else {
      <p class="atmos-paragraph">
        You have selected the following items. Click on an item to view it in a new window.<br/>
        You can share a link to this page with up to 100 items selected.
      </p>
      <div class="atmos-item-unlock-calc__buttons">
        <button type="button" class="atmos-btn atmos-btn--sm" (click)="shareSelection()" [disabled]="!items.length">
          <mat-icon>share</mat-icon> Share
        </button>
        <button type="button" class="atmos-btn atmos-btn--sm" (click)="createCollection()">
          <mat-icon>collections_bookmark</mat-icon> Create collection
        </button>
        <button type="button" class="atmos-btn atmos-btn--sm" (click)="saveAsGoals()">
          <mat-icon>savings</mat-icon> Save as goals
        </button>
        <button type="button" class="atmos-btn atmos-btn--sm" (click)="askRemoveItems()">
          <mat-icon>delete</mat-icon> Clear
        </button>
      </div>
      <div class="atmos-item-unlock-calc__selection">
        @for (item of items; track item.guid) {
          <div class="atmos-item-unlock-calc__selection-item atmos-tile atmos-tile--removable">
            <app-item-icon (click)="viewItem(item)"
              [item]="item" [opaque]="true" [subIcons]="SUBICONS_ALL"
              [skyTooltip]="item.name" placement="top">
            </app-item-icon>
            <button type="button" class="atmos-tile__remove" (click)="removeItem(item)"
              [attr.aria-label]="'Remove ' + item.name" [skyTooltip]="'Remove ' + item.name">✕</button>
          </div>
        }
      </div>
    }
  </div>
</section>

<!-- Results -->
@if (items.length) {
  <section class="atmos-card">
    <div class="atmos-card__body">
      <h2 class="atmos-section__title">Breakdown</h2>

      @if (ownedItems.length) {
        <div>
          <p class="atmos-paragraph">
            You already own some of the selected items. These items will not be counted in the total cost.
          </p>
          <div class="atmos-item-unlock-calc__owned">
            @for (item of ownedItems; track item.guid) {
              <div class="atmos-item-unlock-calc__owned-item">
                <app-item-icon
                  [item]="item" [opaque]="true"
                  [skyTooltip]="item.name" placement="top"
                  [subIcons]="SUBICONS_ALL">
                </app-item-icon>
              </div>
            }
          </div>
        </div>
      }

      @if (trees.length) {
        <div>
          <button type="button" class="atmos-btn atmos-btn--sm"
            [class.atmos-btn--active]="showAsTrees" (click)="toggleShowAsTrees()">
            <mat-icon>account_tree</mat-icon> Show spirit trees
          </button>
          <p class="atmos-paragraph">
            The spirit trees show the cost of all the items you need to get your selection.
          </p>
          @if (showAsTrees) {
            <div class="atmos-spirit-trees">
              @for (tree of trees; track tree.guid) {
                <app-spirit-tree [tree]="tree" [forceNodeAction]="'emit'"
                  [enableControls]="false"
                  [opaqueNodes]="treeOpaqueNodes[tree.guid]"
                  [highlightItem]="treeHighlightItems[tree.guid]"
                  (nodeClicked)="onTreeNodeClicked($event)">
                </app-spirit-tree>
              }
            </div>
          }
        </div>
      }

      @if (results.length) {
        <div class="atmos-item-unlock-calc__results">
          @for (result of results; track result.item.guid) {
            <div class="atmos-item-unlock-calc__result">
              <div class="atmos-item-unlock-calc__result-name">{{ result.item.name }}</div>
              <div class="atmos-item-unlock-calc__result-icon">
                <app-item-icon
                  [item]="result.item" [opaque]="true"
                  [skyTooltip]="result.item.name" placement="top"
                  [subIcons]="SUBICONS_ALL">
                </app-item-icon>
              </div>
              <div class="atmos-item-unlock-calc__result-detail">
                @if (result.item.unlocked) {
                  <span>You own this item.</span>
                } @else if (!result.found) {
                  <span>Couldn't find a way to calculate this item.</span>
                } @else if (result.estimatedCost) {
                  <span>
                    This item hasn't returned before.<br/>
                    @if (result.hasUnknownCost) {
                      The average cost of '{{ result.item.type | itemType }}' is unknown.
                    } @else {
                      The average cost of '{{ result.item.type | itemType }}' is:
                      <app-cost [cost]="result.estimatedCost" [showZeroes]="true" [showTooltip]="false"></app-cost>
                    }
                  </span>
                } @else if (result.cost) {
                  <span>Unlock cost</span>&ngsp;
                  @if ((result.nodes?.length || 0) > 1) {
                    <button type="button" class="atmos-text-link" (click)="showCostBreakdown(result)">(breakdown)</button>
                  } @else if (result.isEmptyCost) {
                    <button type="button" class="atmos-text-link" (click)="showNoCostInfo(result)">(?)</button>
                  }
                  <br/>
                  <app-cost [cost]="result.cost" [showZeroes]="false" [showTooltip]="false"></app-cost>
                } @else if ($any(result).price >= 0) {
                  <span>Price (USD)</span><br/>
                  $ {{ result.price | number: '1.2-2' }}
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  </section>

  <!-- Total -->
  <section class="atmos-card">
    <div class="atmos-card__body">
      <h2 class="atmos-section__title">Total</h2>
      <p class="atmos-paragraph">
        Total cost:
        <app-cost [cost]="totalCost" [showZeroes]="true" [showTooltip]="false" [price]="totalPrice || 0"></app-cost>
      </p>
      @if (totalCostIncludesOngoingSeason) {
        <p class="atmos-paragraph">
          The total cost includes items from the ongoing season.<br/>
          To see if you can still get these items, please refer to the
          <a class="atmos-text-link" href="/season/calculator" target="_blank">season calculator</a>.
        </p>
      }
      @if (totalCostIncludesOngoingEvent) {
        <p class="atmos-paragraph">
          The total cost includes items from the ongoing event.<br/>
          To see if you can still get these items, please refer to the
          <a class="atmos-text-link" href="/event/calculator" target="_blank">event calculator</a>.
        </p>
      }
      @if (totalCostIncludesEstimates) {
        <p class="atmos-paragraph">
          This cost is an estimate as some items have never returned!<br/>
          The estimated costs are based on the average cost of items in the same category.<br/>
          Please keep in mind that additional costs such as blessings are not included in the estimated item prices!<br/>
          For a more realistic price, compare the item to similar items that have returned, or ask the community on Discord!
        </p>
      }
    </div>
  </section>
}

<!-- Add favourites -->
<app-item-unlock-calculator-favourites
  (itemClicked)="onItemClicked($event)" (itemsClicked)="onItemsClicked($event)">
</app-item-unlock-calculator-favourites>

<!-- Add spirits -->
<app-item-unlock-calculator-spirits (spiritSelected)="onSpiritSelected($event)">
</app-item-unlock-calculator-spirits>

<!-- Add seasons -->
<app-item-unlock-calculator-seasons
  (seasonSelected)="onSeasonSelected($event)" (guideSelected)="onSeasonGuideSelected($event)">
</app-item-unlock-calculator-seasons>

<!-- Add events -->
<app-item-unlock-calculator-events (eventSelected)="onEventSelected($event)">
</app-item-unlock-calculator-events>

<!-- Add items -->
<div class="atmos-item-unlock-calc__expand">
  <button type="button" class="atmos-btn" [class.atmos-btn--active]="showAddItems()" (click)="toggleAddItems()">
    <mat-icon>{{ showAddItems() ? 'expand_less' : 'add' }}</mat-icon> Add items
  </button>
</div>

@if (showAddItems()) {
  <div class="atmos-item-unlock-calc__items-wrap">
    <app-item-grid-layout [items]="pickerItems" [itemTemplate]="pickTpl"
      [initialCategory]="itemType" [syncCategoryToUrl]="false"
      (categoryChanged)="onItemTypeChanged($event)"
      (shownItemsChanged)="onItemsChanged($event)"/>
    <ng-template #pickTpl let-item>
      <div class="atmos-item-unlock-calc__pick atmos-tile atmos-tile--selectable"
        [class.is-selected]="itemSet.has(item)" [class.is-unlocked]="item.unlocked"
        (click)="onItemClicked({ event: $event, item })">
        <app-item-icon [item]="item" [subIcons]="SUBICONS_ALL" [skyTooltip]="item.name" placement="auto"></app-item-icon>
        @if (itemSet.has(item)) { <span class="atmos-tile__check">✓</span> }
      </div>
    </ng-template>
    <div class="atmos-item-unlock-calc__buttons">
      <button type="button" class="atmos-btn atmos-btn--sm" (click)="addItemsFromList()">
        @if (itemListItems.length === 1) {
          Add this {{ itemType | itemType:false | lowercase }}
        } @else {
          Add these {{ itemListItems.length }} {{ itemType | itemType:true | lowercase }}
        }
      </button>
    </div>
  </div>
}
